import { createTheme } from '@mui/material/styles';

const baseOptions = {
    palette: {
        text: {
            primary: '#333',
            secondary: 'rgba(0, 0, 0, 0.54)',
            hint: 'rgba(0, 0, 0, 0.38)'
        },
        grey: {
            A100: '#d5d5d5'
        },
        highlight: {
            blue: '#98BAFF',
            green: '#26CC6F',
            red: '#E64040'
        },
        tag: {
            background: '#EEEEEE',
            foreground: '#999999'
        },
        action: {
            active: '#413BBE',
            hover: '#EFF8FD',
            selected: '#E1F0FB'
        },
        validation: {
            error: '#FF4242', // is empty when required, or bad data
            suggested: '#FF8800', // is empty when suggested
            optional: '#990CFF', // highlight changes for approval
            good: '#14A554'
        },
        contentBackground: {
            none: '#EBF6FD'
        },
        contentForeground: {
            none: '#35327c'
        },
        basePrimary: {
            none: '#35327c'
        },
        baseSecondary: {
            none: '#413BBE'
        },
        baseTertiary: {
            none: '#92A5E8'
        },
        custom: {
            darkishGrey: '#555',
            lightishGrey: '#D5D5D5',
            mildlyDarkerGrey: '#bdbdbd',
            veryLightGrey: '#f5f5f5'
        },
        legend: {
            transfers: {
                newColor: '#60CEB9',
                urgentColor: '#ee1cc5',
                updatedColor: '#ee9a29',
                policeTransferColor: '#38BDEE'
            }
        },
        button: {
            alt: {
                backgroundColor: '#2B5FC8',
                foregroundColor: '#FFF',
                hoverBackgroundColor: '#4C6B99'
            },
            outline: {
                backgroundColor: 'none',
                foregroundColor: '#2B5FC8',
                borderColor: '#2B5FC8',
                hoverBackgroundColor: '#EEE'
            },
            icon: {
                foregroundColor: '#CDCDCD'
            },
            save: '#14A554',
            destroy: '#bb5a5a'
        },
        table: {
            header: {
                backgroundColor: '#413BBE',
                foregroundColor: '#FFFFFF'
            },
            headerSecondary: {
                backgroundColor: '#eeeeee',
                foregroundColor: '#555555'
            },
            row: {
                backgroundColor: '#EFF8FD',
                backgroundAltColor: '#DCE4F0',
                hoverColor: 'white'
            },
            nestedRow: {
                backgroundColor: '#FFFFFF',
                hoverColor: 'white'
            },
            border: '#92A5E8'
        }
    },
    sizes: {
        headerHeight: 72,
        footerHeight: 80,
        drawerWidth: 250,
        tasksDrawerWidth: 350,
        drawerWidthMinimized: 50,
        tabHeight: 56
    },
    funeralHome: 'none',
    typography: {
        // Override only values that differ between v3.9.x and v4 defaults.
        // v3.9.x h1-h6 fontSizes match v4 defaults, but lineHeight and color differ.
        // v3.9.x body1/body2 differ from v4 (v4 swapped their meanings).
        h1: { lineHeight: 1, color: '#333' }, // v4: lineHeight 1.167, no color
        h2: { lineHeight: 1, color: '#333' }, // v4: lineHeight 1.2
        h3: { lineHeight: 1.04, color: '#333' }, // v4: lineHeight 1.167
        h4: { lineHeight: 1.17, color: '#333' }, // v5 default letterSpacing 0.00735em matches UAT
        h5: { lineHeight: 1.33, color: '#333' }, // v4: lineHeight 1.334 (close)
        h6: { letterSpacing: 'normal', color: '#333' }, // v5 defaults (1.25rem/1.6) match UAT
        subtitle1: { color: '#333' }, // v5 default letterSpacing 0.00714em matches UAT
        subtitle2: { color: '#333' }, // v4: same
        body1: { lineHeight: 1.46429, letterSpacing: 'normal', color: '#333' }, // fontSize stays default 1rem for MUI internals; MuiTypography.body1 override handles the 0.875rem display size
        body2: { fontWeight: 500, lineHeight: 1.71429, letterSpacing: 'normal', color: '#333' }, // v4: 400, 1.43, 0.01071em
        button: { lineHeight: 'normal', letterSpacing: 'normal', color: '#333' }, // v4: 1.75, 0.02857em
        caption: { lineHeight: 1.375, letterSpacing: 'normal', color: 'rgba(0, 0, 0, 0.54)' }, // v4: 1.66, 0.03333em, no color
        overline: { color: '#333' }, // v4: same otherwise
        fontSizes: {
            regular: '14px'
        }
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            // dt: 1140,
            lg: 1280,
            xl: 1536,
            xxl: 1920
        }
    },
    // Restore v3 shadows — v4/v5 reversed the sub-shadow order, assigning the highest opacity
    // to the tightest blur instead of the broadest, making shadows subtler than v3.
    shadows: [
        'none',
        '0px 1px 3px 0px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 2px 1px -1px rgba(0,0,0,0.12)',
        '0px 1px 5px 0px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 3px 1px -2px rgba(0,0,0,0.12)',
        '0px 1px 8px 0px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 3px 3px -2px rgba(0,0,0,0.12)',
        '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
        '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
        '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
        '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
        '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
        '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
        '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
        '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
        '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
        '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
        '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
        '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
        '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
        '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
        '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
    ],
    validationLabel: {
        lineHeight: '1 !important',
        position: 'absolute',
        top: '100%',
        border: '1px solid #E64040',
        background: 'white',
        padding: '2px 4px',
        display: 'block',
        zIndex: 2,
        fontSize: 12,
        margin: '-1px 5px',
        borderRadius: '0 0 3px 3px',
        maxWidth: 'calc(100% - 10px)',
        minWidth: 64
    },
    components: {
        // Restore v3 CssBaseline behavior
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    lineHeight: 'normal',
                    letterSpacing: 'normal',
                    color: 'inherit'
                },
                'strong, b': {
                    fontWeight: 500 // match index.css strong{font-weight:500}; v5 CssBaseline default is fontWeightBold (700)
                }
            }
        },
        // Restore v3 Typography styles
        MuiTypography: {
            styleOverrides: {
                root: {
                    display: 'block',
                    letterSpacing: '-0.0175rem'
                },
                body1: {
                    fontSize: '0.875rem'
                }
            }
        },
        MuiFormControl: {
            defaultProps: {
                variant: 'standard' // v5 changed default to outlined
            },
            styleOverrides: {
                root: {
                    marginBottom: 10
                }
            }
        },
        MuiTextField: {
            defaultProps: {
                variant: 'standard' // v5 changed default to outlined
            }
        },
        MuiSelect: {
            defaultProps: {
                variant: 'standard' // v5 changed default to outlined
            },
            styleOverrides: {
                select: {
                    minHeight: 21
                }
            }
        },
        // Restore v3 FormLabel lineHeight (v5 sets 1.4375em, v3 used 1)
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    lineHeight: 1
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                formControl: {
                    transform: 'translate(0, 24px) scale(1)'
                },
                shrink: {
                    transform: 'translate(0, 1.5px) scale(0.75)'
                }
            }
        },
        // Restore v3 InputBase lineHeight (v5 sets 1.4375em, v3 used 1.1875em)
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '1rem', // v3 default; v5 inherits from parent which may be smaller (e.g. TableCell 0.8125rem)
                    lineHeight: '1.1875em'
                },
                input: {
                    '&[type="date"], &[type="time"]': {
                        padding: '6px 0',
                        height: 21,
                        boxSizing: 'content-box'
                    }
                }
            }
        },
        // Restore v3 ListItem padding (v4 changed from 11px to 8px)
        MuiListItem: {
            styleOverrides: {
                root: {
                    paddingTop: 11,
                    paddingBottom: 11
                },
                dense: {
                    paddingTop: 8,
                    paddingBottom: 8
                },
                button: {
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)'
                    }
                },
                secondaryAction: {
                    paddingRight: 32
                }
            }
        },
        // Restore v3 ListItemIcon
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    marginRight: 16,
                    minWidth: 'auto'
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    padding: '0 16px',
                    marginTop: 0,
                    marginBottom: 0,
                    '&:first-of-type': {
                        paddingLeft: 0
                    }
                },
                inset: {
                    '&:first-of-type': {
                        paddingLeft: 56
                    }
                },
                dense: {
                    fontSize: '0.8125rem'
                },
                primary: {
                    lineHeight: 1.5
                }
            }
        },
        // Restore v3 Tab scroll button width
        MuiTabScrollButton: {
            styleOverrides: {
                root: {
                    width: 56
                }
            }
        },
        // Restore v4 Tabs defaults (v5 changed textColor from 'inherit' to 'primary',
        // indicatorColor from 'secondary' to 'primary')
        MuiTabs: {
            defaultProps: {
                textColor: 'inherit',
                indicatorColor: 'secondary'
            }
        },
        // Restore v3 Tab styles
        MuiTab: {
            styleOverrides: {
                root: {
                    lineHeight: 'normal', // v5 sets 1.25, v3 inherited 'normal'
                    '@media (min-width:960px)': {
                        fontSize: '0.8125rem',
                        minWidth: 140,
                        padding: '12px 6px'
                    }
                },
                textColorInherit: {
                    opacity: 0.7, // v3 used opacity:0.7 for unselected inherit tabs (v5 changed to 0.6)
                    '&.Mui-selected': {
                        opacity: 1
                    }
                }
            }
        },
        // Restore v3 Button styles
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem', // v5 changed to 0.9375rem
                    lineHeight: 1.75
                },
                text: {
                    '&:hover': {
                        backgroundColor: 'rgba(51, 51, 51, 0.08)'
                    }
                },
                outlined: {
                    padding: '5px 16px',
                    border: '1px solid rgba(0, 0, 0, 0.26)'
                }
            }
        },
        // Restore v3 SvgIcon/Icon size
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: 24
                }
            }
        },
        MuiIcon: {
            styleOverrides: {
                root: {
                    fontSize: 24
                }
            }
        },
        // Restore v3 MenuItem styles
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem'
                }
            }
        },
        // Restore v3 TableRow header height (DevExpress v1 set 56px, v4 doesn't)
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: 48
                },
                head: {
                    height: 56
                }
            }
        },
        // Restore v3 TableCell styles
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    lineHeight: 1.46429
                },
                body: {
                    paddingTop: 4,
                    paddingBottom: 4
                },
                head: {
                    textTransform: 'uppercase',
                    fontSize: '0.725rem',
                    fontWeight: 500,
                    letterSpacing: '-0.033rem',
                    color: 'rgba(0, 0, 0, 0.54)'
                }
            }
        },
        // Restore v4 TableSortLabel icon size (v5 changed from 16 to 18)
        MuiTableSortLabel: {
            styleOverrides: {
                icon: {
                    fontSize: 16
                }
            }
        },
        // Restore v3 AccordionSummary styles
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    padding: '0 24px'
                },
                content: {
                    '& > :last-child': {
                        paddingRight: 32
                    }
                },
                expandIconWrapper: {
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%) rotate(0deg)',
                    marginRight: 0,
                    '&.Mui-expanded': {
                        transform: 'translateY(-50%) rotate(180deg)'
                    }
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    padding: 12
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: 12
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: -14
                }
            }
        },
        // Restore v3 default props
        MuiPaper: {
            defaultProps: {
                elevation: 2
            },
            styleOverrides: {
                root: {
                    color: 'inherit', // v3 Paper didn't set color; v5 adds palette.text.primary
                    '&.MuiPaper-elevation0.MuiPaper-rounded': {
                        borderRadius: 8,
                        border: '1px solid #d5d5d5'
                    }
                }
            }
        },
        MuiGrid2: {
            styleOverrides: {
                root: { minWidth: 'auto' } // prevent item overlap
            }
        }
    }
};
export const theme = createTheme(baseOptions);

export const recolour = (originalColour, comparisonColour = '#FFFFFF', percentDiff = 0.9) => {
    originalColour = /^#[0-9A-F]{6}$/i.test(originalColour) ? originalColour : '#000000';
    const f = percentDiff <= 1 ? percentDiff : percentDiff / 100;
    let c0 = (originalColour || '').replace(/#/, '');
    let c1 = (comparisonColour || '').replace(/#/, '');
    c0 = c0.match(/.{1,2}/g).map(oct => parseInt(oct, 16) * (1 - f));
    c1 = c1.match(/.{1,2}/g).map(oct => parseInt(oct, 16) * f);
    let ci = [0, 1, 2].map(i => Math.min(Math.round(c0[i] + c1[i]), 255));
    return (
        '#' +
        ci
            .reduce((a, v) => (a << 8) + v, 0)
            .toString(16)
            .padStart(6, '0')
    );
};
